/**
 * IndexedDB helper for storing File objects across page navigations
 * Used for persisting attachments during auth flow
 */

const DB_NAME = 'heynicolai_files'
const DB_VERSION = 1
const STORE_NAME = 'pending_attachments'
const PENDING_KEY = 'precadrage_files'

interface StoredFile {
  name: string
  type: string
  size: number
  data: ArrayBuffer
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

/**
 * Store files in IndexedDB for later retrieval after auth
 */
export async function storePendingFiles(files: File[]): Promise<void> {
  if (files.length === 0) {
    await clearPendingFiles()
    return
  }

  const db = await openDatabase()

  // Convert File objects to storable format
  const storedFiles: StoredFile[] = await Promise.all(
    files.map(async (file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      data: await file.arrayBuffer(),
    }))
  )

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.put(storedFiles, PENDING_KEY)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })
}

/**
 * Retrieve pending files from IndexedDB and convert back to File objects
 */
export async function getPendingFiles(): Promise<File[]> {
  try {
    const db = await openDatabase()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(PENDING_KEY)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const storedFiles = request.result as StoredFile[] | undefined
        if (!storedFiles || storedFiles.length === 0) {
          resolve([])
          return
        }

        // Convert back to File objects
        const files = storedFiles.map(
          (sf) => new File([sf.data], sf.name, { type: sf.type })
        )
        resolve(files)
      }
    })
  } catch {
    return []
  }
}

/**
 * Clear pending files from IndexedDB
 */
export async function clearPendingFiles(): Promise<void> {
  try {
    const db = await openDatabase()

    return new Promise((resolve) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(PENDING_KEY)

      request.onerror = () => resolve() // Ignore errors when clearing
      request.onsuccess = () => resolve()
    })
  } catch {
    // Ignore errors when clearing
  }
}
